import {
    Box,
    Button,
    Text,
    Tooltip,
    HStack,
    VStack,
    Collapse,
    Spinner,
} from '@chakra-ui/react';
import React, { useContext, useMemo } from 'react';
import MapContext from '../../context/MapContext';
import { Icon } from '@blueprintjs/core';
import { TbAlertTriangle } from 'react-icons/tb';
import ElevationChart from '../ElevationChart';
import samplesToChartData from '../../utils/elevation/samplesToChartData';
import {
    formatDistance,
    distanceUnitsFromDepthUnits,
} from '../../utils/formatting/units';

function MeasureDisplay() {
    const {
        rulerControlRef,
        measureState,
        elevationState,
        elevationGraphVisible,
        setElevationGraphVisible,
        depthUnits,
    } = useContext(MapContext);

    const samples = elevationGraphVisible ? elevationState?.samples || [] : [];
    const elevationSamples = samplesToChartData(samples);
    const status = elevationState?.status || 'idle';
    const pointCount = measureState?.coordinates?.length || 0;
    const distanceUnits = distanceUnitsFromDepthUnits(depthUnits);
    const totalDistance = useMemo(() => {
        const meters =
            typeof measureState?.lengthMeters === 'number'
                ? measureState.lengthMeters
                : 0;
        return formatDistance(meters, distanceUnits, 2, true, true);
    }, [measureState?.lengthMeters, distanceUnits]);

    const isLoading = status === 'loading';
    const showEmptyOverlay =
        elevationGraphVisible &&
        (status === 'idle' ||
            isLoading ||
            status === 'error' ||
            !samples.length);

    let emptyTitle = 'No elevation profile';
    let emptyDetail = 'Add two or more measurement points.';
    if (status === 'error') {
        emptyTitle = 'Elevation unavailable';
        emptyDetail =
            elevationState?.error?.message || 'Elevation request failed.';
    }

    return (
        <Box
            pointerEvents="auto"
            zIndex={20}
            display="flex"
            flexDir="column"
            justifyContent="stretch"
            alignItems="stretch"
            bg="rgba(255,255,255,0.9)"
            borderRadius="4px"
            border="1px solid rgba(0,0,0,0.1)"
            shadow="md"
            padding="0px"
            overflow="hidden"
            boxShadow={'0px 0px 0px 2px rgba(0,0,0,0.1)'}
            position="relative"
            minW="450px"
        >
            <HStack spacing={1} p="10px" pr="44px">
                <Text fontWeight="500" fontSize="14px">
                    Measure Tool
                </Text>
                <Text fontWeight="400" fontSize="14px">
                    -
                </Text>
                <Text fontWeight="400" fontSize="14px">
                    Click to add points
                </Text>
            </HStack>
            <Box
                bg="rgba(0,0,0,0.05)"
                w="100%"
                borderTop="1px solid rgba(0,0,0,0.1)"
                borderBottom={
                    elevationGraphVisible && '1px solid rgba(0,0,0,0.1)'
                }
                p="8px"
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
            >
                <Text
                    flex="0 0 auto"
                    mr="10px"
                    fontFamily="monospace"
                    fontWeight="600"
                    fontSize="14px"
                >
                    {totalDistance}
                </Text>
                <Text
                    flex="0 0 auto"
                    fontFamily="monospace"
                    fontWeight="400"
                    fontSize="14px"
                    opacity="0.6"
                >
                    ({pointCount} point{pointCount !== 1 && 's'})
                </Text>
                <Button
                    onClick={() =>
                        setElevationGraphVisible(!elevationGraphVisible)
                    }
                    size="sm"
                    variant="ghost"
                    fontWeight="400"
                    position="absolute"
                    right="0px"
                    _hover={{
                        bg: 'none',
                    }}
                    _active={{
                        bg: 'none',
                    }}
                >
                    <Text opacity="0.5" ml="4px" mr="4px" fontWeight="400">
                        <Icon icon="timeline-line-chart" />
                    </Text>
                    {elevationGraphVisible ? 'Hide' : 'Show'} Elevation
                </Button>
            </Box>
            <Box position="relative">
                {showEmptyOverlay && (
                    <Box
                        zIndex={1}
                        position="absolute"
                        top="0px"
                        bottom="0px"
                        left="0px"
                        right="0px"
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        bg="rgba(245,245,245,0.8)"
                    >
                        {isLoading ? (
                            <Spinner
                                size="md"
                                color="blue.500"
                                thickness="3px"
                            />
                        ) : (
                            <VStack spacing={0}>
                                <Text color="black" opacity="0.2">
                                    <Icon icon="mountain" size="48" />
                                </Text>
                                <Text opacity="0.8">{emptyTitle}</Text>
                                <Text textAlign="center" px="16px">
                                    {emptyDetail}
                                </Text>
                            </VStack>
                        )}
                    </Box>
                )}
                <Collapse in={elevationGraphVisible} animateOpacity>
                    <Box position="relative" h="140px" minH="140px" w="100%">
                        <ElevationChart data={elevationSamples} />
                        <HStack
                            position="absolute"
                            left="50%"
                            bottom="4px"
                            transform="translateX(-50%)"
                            spacing="4px"
                            pointerEvents="none"
                            color="rgba(0,0,0,0.35)"
                            alignItems="center"
                        >
                            <Box as={TbAlertTriangle} fontSize="14px" />
                            <Text fontSize="14px" fontWeight="400" whiteSpace="nowrap">
                                elevation is approximate.
                            </Text>
                        </HStack>
                    </Box>
                </Collapse>
            </Box>
            <Tooltip placement="right" label={<Text>Deselect</Text>}>
                <Button
                    _hover={{
                        color: 'rgba(0,0,0,1.0)',
                    }}
                    bg={''}
                    w="40px"
                    h={'40px'}
                    position="absolute"
                    top="0px"
                    right="0px"
                    onClick={() => {
                        rulerControlRef.current.measuringOff();
                    }}
                    color={'rgba(0,0,0,0.8)'}
                >
                    <Text mb="0px">
                        <Icon icon={'cross'} size="16" />
                    </Text>
                </Button>
            </Tooltip>
        </Box>
    );
}

export default MeasureDisplay;
